import React, {memo, useCallback, useEffect, useRef, useState} from 'react';
import {Editor, Toolbar} from '@wangeditor/editor-for-react';
import {i18nChangeLanguage, IDomEditor, IEditorConfig, IToolbarConfig} from '@wangeditor/editor';
import '@wangeditor/editor/dist/css/style.css'; // 引入 css
import classNames from 'classnames';
// @ts-ignore
import style from '../index.scss';

interface Props {
  getEditorContent: ((v: string, isInited: boolean) => void) | null; // 拿到当前富文本内容
  getEditorText?: (v: string, isInited: boolean) => void; // 拿到当前富文本纯文本内容
  status?: 'error' | 'success';
  errorTips?: string;
  defaultValue?: string;
  placeholder?: string;
  autoFocus?: boolean;
  maxLength?: number;
  customStyle?: React.CSSProperties;
  className?: string;
  isShowToolbar?: boolean;
  readOnly?: boolean;
  maxContentHeight?: number; // 传了这个属性内容超出高度将出现滚动条
  isEn?: boolean;
  toolbarConfig?: Partial<IToolbarConfig>;
  [keyName: string]: any;
}

const DEFAULT_PROPS: Props = {
  getEditorContent: null,
  status: 'success',
  errorTips: '',
  defaultValue: '',
  placeholder: '',
  autoFocus: true,
  maxLength: undefined,
  customStyle: {},
  className: '',
  isShowToolbar: true,
  readOnly: false,
  maxContentHeight: 0,
  isEn: true,
};

function Index(props: Props) {
  const {
    getEditorContent,
    getEditorText,
    status,
    errorTips,
    defaultValue,
    placeholder,
    autoFocus,
    maxLength,
    customStyle,
    className,
    isShowToolbar,
    readOnly = false,
    maxContentHeight,
    isEn,
    toolbarConfig: propsToolbarConfig,
  } = props;
  // 切换语言 - 'en' 或者 'zh-CN'
  i18nChangeLanguage(isEn ? 'en' : 'zh-CN');

  const [editor, setEditor] = useState<IDomEditor | null>(null); // 存储 editor 实例
  const [html, setHtml] = useState<string>(''); // 编辑器内容
  const [isInit, setIsInit] = useState<boolean>(true); // 记录是否为初始化
  // 时间防抖
  const [startTime, setStartTime] = useState<number>(0);
  const currentWangEditorRef = useRef<HTMLDivElement>(null);

  // 查看配置信息
  // console.log(DomEditor.getToolbar(editor));

  // 工具栏配置
  const toolbarConfig: Partial<IToolbarConfig> = propsToolbarConfig ? propsToolbarConfig : {
    toolbarKeys: [
      // "header1",
      // "header2",
      // "header3",
      // "headerSelect",
      "bold",
      "underline",
      // 'italic',
      "through",
      // "color",
      // "bgColor",
      "clearStyle",
      "undo",
      "redo",
    ],
    excludeKeys: [],
  };

  // 编辑器配置
  const editorConfig: Partial<IEditorConfig> = {
    placeholder,
    autoFocus,
    scroll: true,
    maxLength,
  };

  // 默认数据展示
  useEffect(() => {
    // console.log(defaultValue, html);
    if (defaultValue == html || ((new Date().getTime()) - startTime < 500)) return;
    
    let newHtml = '';
    if (defaultValue && defaultValue?.slice(0, 1) === '<' && defaultValue?.slice(-1) === '>') {
      newHtml = defaultValue || '';
    } else {
      newHtml = defaultValue ? `<p>${defaultValue}</p>` : '';
    }
    
    // 如果编辑器已创建，使用 editor.setHtml() 来确保状态同步
    if (editor && newHtml !== html) {
      try {
        // 使用 requestAnimationFrame 确保在下一个渲染周期执行
        requestAnimationFrame(() => {
          try {
            editor.setHtml(newHtml);
            setHtml(newHtml);
          } catch (error) {
            console.warn('wangEditor setHtml error:', error);
            // 如果设置失败，回退到直接设置 state
            setHtml(newHtml);
          }
        });
      } catch (error) {
        console.warn('wangEditor setHtml error:', error);
        setHtml(newHtml);
      }
    } else {
      setHtml(newHtml);
    }
    setStartTime((new Date().getTime()));
    setIsInit(true);
  }, [defaultValue]);

  useEffect(() => {
    if (!editor) return;
    try {
      // console.log(editor?.isEmpty(), editor?.getText(), editor);
      const isEmpty = html === '<p><br></p>' || editor?.isEmpty() || !html;
      if (isInit && !isEmpty) {
        setIsInit(false);
        getEditorContent?.(isEmpty ? '' : html, false);
        getEditorText?.(editor?.getText() || '', false);
      }
      if (isInit) return;
      getEditorContent?.(isEmpty ? '' : html, true);
      getEditorText?.(editor?.getText() || '', true);
    } catch (error) {
      console.warn('wangEditor content sync error:', error);
      // 发生错误时，尝试使用当前 html 状态
      getEditorContent?.(html || '', true);
      getEditorText?.('', true);
    }
  }, [html, editor, isInit, getEditorContent, getEditorText]);

  // 及时销毁 editor ，重要！
  useEffect(() => () => {
    if (editor == null) return;
    editor?.destroy();
    setEditor(null);
  }, [editor]);

  // 切换编辑器是否只读
  useEffect(() => {
    if (editor === null) return;
    if (readOnly) {
      editor?.disable();
    } else {
      editor?.enable();
    }
  }, [editor, readOnly]);

  // 设置内容滚动
  useEffect(() => {
    if (!maxContentHeight || !currentWangEditorRef.current) return;
    // @ts-ignore
    const WEScrollContent: HTMLDivElement[] = currentWangEditorRef?.current?.getElementsByClassName('w-e-scroll');
    if (WEScrollContent) {
      WEScrollContent[0].style.maxHeight = `${maxContentHeight}px`;
    }
  }, [maxContentHeight]);

  return (
    <>
      <section
        className={classNames({
          [style['editor-content']]: true,
          // 'gp-wang-edit-content': true,
          [style['error-status']]: status === 'error',
          [className as string]: !!className,
        })}
        style={customStyle}
        ref={currentWangEditorRef}
      >
        {!!isShowToolbar && (
          <Toolbar
            editor={editor}
            defaultConfig={toolbarConfig}
            mode="simple"
            style={{ borderBottom: '1px solid #ddd' }}
          />
        )}
        <Editor
          defaultConfig={editorConfig}
          value={html}
          onCreated={setEditor}
          onChange={(editorInstance: IDomEditor) => {
            try {
              // 使用 requestAnimationFrame 确保编辑器状态已完全同步
              requestAnimationFrame(() => {
                try {
                  const currentHtml = editorInstance?.getHtml() || '';
                  setHtml(currentHtml);
                } catch (error) {
                  console.warn('wangEditor onChange getHtml error:', error);
                  // 如果获取 HTML 失败，尝试从编辑器的 DOM 中获取
                  try {
                    const editorDom = editorInstance?.getEditableContainer();
                    if (editorDom) {
                      setHtml(editorDom.innerHTML || '');
                    }
                  } catch (domError) {
                    console.warn('wangEditor onChange DOM error:', domError);
                  }
                }
              });
            } catch (error) {
              console.warn('wangEditor onChange error:', error);
            }
          }}
          mode="simple"
        />
      </section>
      <label
        className={classNames({
          [style['error-info']]: status === 'error',
        })}
        style={{ display: 'none' }}
      >{errorTips || 'Please check that the information is complete or correct.'}</label>
    </>
  );
}

Index.defaultProps = DEFAULT_PROPS;

export default memo(Index);
